// ===========================================
// Program Level Distribution Chart Component
// ===========================================
// Pie chart showing student distribution across program levels
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Paper, Typography, Box, Skeleton } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { ProgramLevelDistRecord } from '../../api/reports.api';

interface ProgramLevelDistChartProps {
  data?: ProgramLevelDistRecord[];
  isLoading: boolean;
}

// Brand colors for each program level
const PROGRAM_LEVEL_COLORS: { [key: string]: string } = {
  MINI: '#96DAC9', // Mint
  MASTER: '#4580E4', // Primary Blue
  MEZZO: '#FFCE00', // Yellow
  MOLTO: '#FFAE9E', // Coral
  MAESTRO: '#5cb399', // Green
  VOICE: '#a3d9f6', // Light Blue
};

// Fallback color array for dynamic program levels
const DEFAULT_COLORS = ['#4580E4', '#FFCE00', '#96DAC9', '#FFAE9E', '#5cb399', '#a3d9f6'];

const ProgramLevelDistChart: React.FC<ProgramLevelDistChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Student Distribution by Program Level
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Student Distribution by Program Level
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No program level distribution data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  const chartData = data.map((level) => ({
    name: level.levelName,
    value: level.studentCount,
    percentage: level.percentageOfTotal,
    averageProgress: level.averageProgress,
  }));

  const totalStudents = data.reduce((sum, level) => sum + level.studentCount, 0);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Student Distribution by Program Level
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total Students: {totalStudents}
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(entry: any) => `${entry.name} (${entry.percentage.toFixed(1)}%)`}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PROGRAM_LEVEL_COLORS[entry.name] || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{data.name}</Typography>
                    <Typography variant="body2">Students: {data.value}</Typography>
                    <Typography variant="body2">
                      Percentage: {data.percentage.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" display="block">
                      Avg Progress: {data.averageProgress.toFixed(1)}%
                    </Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default ProgramLevelDistChart;
